import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import {
  api,
  ApiError,
  type AiLogicFields,
  type ImportCandidate,
  type ImportFolder,
  type ProjectDetailResponse,
  type ProjectHealthResponse,
  type ProjectHandoffResponse,
  type ProjectResponse,
  type ProjectSummaryResponse,
  type SourceStateResponse,
  type Userbot,
} from '@/lib/api'
import { HistoryScanControl } from '@/components/HistoryScanControl'
import { buildProjectNumberMap, projectLabel } from '@/lib/projectLabels'
import { ConnectUserbotModal } from '@/components/ConnectUserbotModal'
import { useAuth } from '@/lib/auth'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function Spinner() {
  return (
    <div
      style={{
        width: 16,
        height: 16,
        borderRadius: '50%',
        border: '2px solid var(--line-2)',
        borderTopColor: 'var(--accent)',
        animation: 'spin 0.7s linear infinite',
        flexShrink: 0,
      }}
    />
  )
}

function InlineFeedback({
  ok,
  err,
}: {
  ok: boolean
  err: string | null
}) {
  if (ok) {
    return (
      <span style={{ fontSize: 12, color: 'var(--success-ink)' }}>
        Сохранено
      </span>
    )
  }
  if (err) {
    return (
      <span style={{ fontSize: 12, color: 'var(--danger-ink)', wordBreak: 'break-word' }}>
        {err}
      </span>
    )
  }
  return null
}

function apiErrorMessage(err: unknown, fallback = 'Ошибка'): string {
  if (err instanceof ApiError) {
    try {
      const parsed = JSON.parse(err.message)
      if (parsed && typeof parsed.detail === 'string') return parsed.detail
    } catch {
      /* plain text */
    }
    return err.message || fallback
  }
  return err instanceof Error ? err.message : fallback
}

const PROJECT_LIST_WIDTH_KEY = 'sensor.projects.listWidth'
const PROJECT_DRAWER_WIDTH_KEY = 'sensor.projects.drawerWidth'
const PROJECT_LIST_WIDTH_MIN = 420
const PROJECT_DRAWER_WIDTH_MIN = 340
const PROJECT_DRAWER_WIDTH_MAX = 760
const PROJECT_DRAWER_WIDTH_DEFAULT = 420

type ProjectSplitStyle = CSSProperties & {
  '--project-list-width'?: string
  '--project-drawer-width'?: string
}

function clampProjectDrawerWidth(width: number): number {
  return Math.max(PROJECT_DRAWER_WIDTH_MIN, Math.min(PROJECT_DRAWER_WIDTH_MAX, Math.round(width)))
}

function useProjectSplitResize() {
  const layoutRef = useRef<HTMLDivElement>(null)
  const [listWidth, setListWidth] = useState<number | null>(() => {
    if (typeof window === 'undefined') return null
    const stored = Number(window.localStorage.getItem(PROJECT_LIST_WIDTH_KEY))
    return Number.isFinite(stored) && stored > 0
      ? Math.max(PROJECT_LIST_WIDTH_MIN, Math.round(stored))
      : null
  })
  const [drawerWidth, setDrawerWidth] = useState(() => {
    if (typeof window === 'undefined') return PROJECT_DRAWER_WIDTH_DEFAULT
    const stored = Number(window.localStorage.getItem(PROJECT_DRAWER_WIDTH_KEY))
    return Number.isFinite(stored) && stored > 0
      ? clampProjectDrawerWidth(stored)
      : PROJECT_DRAWER_WIDTH_DEFAULT
  })

  useEffect(() => {
    window.localStorage.setItem(PROJECT_DRAWER_WIDTH_KEY, String(drawerWidth))
  }, [drawerWidth])
  useEffect(() => {
    if (listWidth != null) {
      window.localStorage.setItem(PROJECT_LIST_WIDTH_KEY, String(listWidth))
    }
  }, [listWidth])

  function startResize(e: ReactPointerEvent<HTMLDivElement>) {
    const box = layoutRef.current?.getBoundingClientRect()
    if (!box) return
    e.preventDefault()

    const resizeTo = (clientX: number) => {
      setListWidth(Math.max(PROJECT_LIST_WIDTH_MIN, Math.round(clientX - box.left)))
      setDrawerWidth(clampProjectDrawerWidth(box.right - clientX - 8))
    }
    const onMove = (event: PointerEvent) => resizeTo(event.clientX)
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  const splitStyle: ProjectSplitStyle = {
    ...(listWidth != null ? { '--project-list-width': `${listWidth}px` } : {}),
    '--project-drawer-width': `${drawerWidth}px`,
  }

  return { layoutRef, drawerWidth, splitStyle, startResize }
}

// ---------------------------------------------------------------------------
// KeywordEditor
// ---------------------------------------------------------------------------

interface KeywordEditorProps {
  label: string
  initialKeywords: string[]
  cap: number
  onSave: (kws: string[]) => Promise<string[]>
  isPending: boolean
}

function normaliseKeywordList(values: string[], cap: number): string[] {
  const seen = new Set<string>()
  const result: string[] = []
  for (const value of values) {
    const trimmed = value.trim()
    if (!trimmed) continue
    const key = trimmed.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    result.push(trimmed)
    if (result.length >= cap) break
  }
  return result
}

function KeywordEditor({ label, initialKeywords, cap, onSave, isPending }: KeywordEditorProps) {
  const initialKeywordsKey = initialKeywords.join('\u0001')
  const [keywords, setKeywords] = useState<string[]>(() => normaliseKeywordList(initialKeywords, cap))
  const [input, setInput] = useState('')
  const [ok, setOk] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false)

  useEffect(() => {
    const values = initialKeywordsKey ? initialKeywordsKey.split('\u0001') : []
    setKeywords(normaliseKeywordList(values, cap))
    setInput('')
    setOk(false)
    setErr(null)
    setNotice(null)
    setClearConfirmOpen(false)
  }, [initialKeywordsKey, cap])

  function addFromInput(raw: string) {
    const parts = raw
      .split(/[,\n;]+/)
      .map((s) => s.trim())
      .filter(Boolean)
    if (parts.length === 0) return

    const seen = new Set(keywords.map((kw) => kw.toLowerCase()))
    const accepted: string[] = []
    let duplicateCount = 0
    let limitCount = 0

    for (const part of parts) {
      const key = part.toLowerCase()
      if (seen.has(key)) {
        duplicateCount += 1
        continue
      }
      if (keywords.length + accepted.length >= cap) {
        limitCount += 1
        continue
      }
      seen.add(key)
      accepted.push(part)
    }

    if (accepted.length > 0) {
      setKeywords((prev) => [...prev, ...accepted])
    }
    setInput('')
    setOk(false)
    setErr(null)

    const skipped = duplicateCount + limitCount
    if (skipped > 0) {
      const reasons = [
        duplicateCount > 0 ? `${duplicateCount} дубл.` : null,
        limitCount > 0 ? `${limitCount} сверх лимита ${cap}` : null,
      ].filter(Boolean)
      setNotice(`Добавлено ${accepted.length}. Не добавлено ${skipped}: ${reasons.join(', ')}.`)
    } else {
      setNotice(null)
    }
  }

  function remove(kw: string) {
    setKeywords((prev) => prev.filter((k) => k !== kw))
    setOk(false)
    setNotice(null)
    setClearConfirmOpen(false)
  }

  async function clearAll() {
    setOk(false)
    setErr(null)
    setNotice(null)
    setClearConfirmOpen(false)
    try {
      const savedKeywords = await onSave([])
      setKeywords(normaliseKeywordList(savedKeywords, cap))
      setOk(true)
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e))
    }
  }

  async function handleSave() {
    setOk(false)
    setErr(null)
    setNotice(null)
    try {
      const savedKeywords = await onSave(keywords)
      const normalisedBeforeSave = normaliseKeywordList(keywords, cap)
      const normalisedSaved = normaliseKeywordList(savedKeywords, cap)
      setKeywords(normalisedSaved)
      if (normalisedSaved.length < normalisedBeforeSave.length) {
        setNotice(
          `Сервер сохранил ${normalisedSaved.length} из ${normalisedBeforeSave.length}. Остальные не прошли лимит или были дублями.`,
        )
      }
      setOk(true)
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e))
    }
  }

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 10.5,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'var(--muted)',
        }}
      >
        {label}
      </div>

      {/* chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {keywords.map((kw) => (
          <span key={kw} className="proj-kw-chip">
            {kw}
            <button
              type="button"
              aria-label={`Удалить ${kw}`}
              onClick={() => remove(kw)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0 0 0 4px',
                color: 'var(--muted)',
                fontSize: 12,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </span>
        ))}
        {keywords.length === 0 && (
          <span style={{ fontSize: 12, color: 'var(--muted-2)' }}>Нет ключевых слов</span>
        )}
      </div>

      {/* input row */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          type="text"
          className="proj-field"
          style={{ flex: 1 }}
          placeholder="Добавить через запятую или Enter…"
          value={input}
          disabled={keywords.length >= cap}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              if (input.trim()) addFromInput(input)
            }
          }}
        />
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          disabled={!input.trim() || keywords.length >= cap}
          onClick={() => addFromInput(input)}
        >
          Добавить
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--sm proj-btn--danger"
          disabled={isPending || keywords.length === 0}
          onClick={() => {
            setNotice(null)
            setClearConfirmOpen((prev) => !prev)
          }}
        >
          Удалить все
        </button>
      </div>
      {clearConfirmOpen && keywords.length > 0 && (
        <div className="proj-delete-confirm" role="alert" style={{ marginTop: 2 }}>
          <div className="proj-delete-confirm__actions" style={{ justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>
              Удалить все {keywords.length} слов из списка?
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => setClearConfirmOpen(false)}
                disabled={isPending}
              >
                Отмена
              </button>
              <button
                type="button"
                className="btn btn--sm proj-btn--danger"
                onClick={() => void clearAll()}
                disabled={isPending}
              >
                Да, удалить все
              </button>
            </div>
          </div>
        </div>
      )}
      {notice && (
        <div style={{ fontSize: 12, color: 'var(--danger-ink)', lineHeight: 1.35 }}>
          {notice}
        </div>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          justifyContent: 'space-between',
        }}
      >
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            color: keywords.length >= cap ? 'var(--danger-ink)' : 'var(--muted)',
          }}
        >
          {keywords.length}/{cap}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <InlineFeedback ok={ok} err={err} />
          <button
            type="button"
            className="btn btn--primary btn--sm"
            disabled={isPending}
            onClick={() => void handleSave()}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            {isPending && <Spinner />}
            Сохранить
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// ProjectDrawer
// ---------------------------------------------------------------------------

type DrawerTab = 'main' | 'keywords' | 'exclude' | 'ai' | 'watch'

const TAB_LABELS: Record<DrawerTab, string> = {
  main: 'Основное',
  keywords: 'Ключевые слова',
  exclude: 'Исключения',
  ai: 'AI-логика',
  watch: 'Мониторинг пользователя',
}

function StatusBadge({ status }: { status: string }) {
  const isActive = status === 'active'
  return (
    <span
      className={`proj__status proj__status--${isActive ? 'active' : status === 'draft' ? 'draft' : 'paused'}`}
    >
      {isActive ? 'Активен' : status === 'draft' ? 'Черновик' : 'Пауза'}
    </span>
  )
}

const LOGIC_TAIL = 'Решай по смыслу, а не по отдельным словам.'
// Legacy rendered tails older stored prompts may still carry (stripped on parse).
const LEGACY_LOGIC_TAILS = ['Decide by meaning, not individual words.']
const AI_PROMPT_MAX_CHARS = 1600
const LOGIC_LABELS = {
  context: 'Контекст:',
  targetAuthor: 'Целевой автор:',
  avoidRole: 'Не путай с ролью:',
  positiveExamples: 'Подходят формулировки вроде:',
  negativeExamples: 'Не считай лидом сообщения вроде:',
} as const

const LOGIC_LABEL_ALIASES = {
  context: ['Context:', 'Контекст:'],
  targetAuthor: ['Target author:', 'Целевой автор:'],
  avoidRole: ['Do not confuse with role:', 'Не путай с ролью:'],
  positiveExamples: ['Good lead examples:', 'Good examples:', 'Подходят формулировки вроде:'],
  negativeExamples: [
    'Do not count as lead examples:',
    "Don't count as lead examples:",
    'Не считай лидом сообщения вроде:',
  ],
} as const

interface LeadLogicSpec {
  lead: string
  context: string
  targetAuthor: string
  avoidRole: string
  positiveExamples: string[]
  negativeExamples: string[]
  structured: boolean
}

type LeadLogicTextField = 'lead' | 'context' | 'targetAuthor' | 'avoidRole'
type LeadLogicExampleField = 'positiveExamples' | 'negativeExamples'
type AiEditMode = 'fields' | 'text'

function emptyLeadLogicSpec(): LeadLogicSpec {
  return {
    lead: '',
    context: '',
    targetAuthor: '',
    avoidRole: '',
    positiveExamples: [],
    negativeExamples: [],
    structured: false,
  }
}

function normalizeLogicText(value: string | null | undefined): string {
  return String(value ?? '').replace(/\s+/g, ' ').trim()
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function stripSentenceSuffix(value: string): string {
  return normalizeLogicText(value).replace(/[ .;:]+$/u, '')
}

function ensureSentence(value: string): string {
  const cleaned = normalizeLogicText(value)
  if (!cleaned) return ''
  return /[.!?…]$/u.test(cleaned) ? cleaned : `${cleaned}.`
}

function splitLogicExamples(value: string | string[]): string[] {
  const raw = Array.isArray(value) ? value.join('\n') : value
  const normalized = raw.replace(/\r\n?/gu, '\n').trim()
  if (!normalized) return []

  const continuationPattern = /^(?:и|а|но|или|либо|что|чтобы|где|как|когда|если|потому\s+что|котор(?:ый|ая|ое|ые|ому|ого|ым|ой|ую|ых|ыми)?)\b/iu
  const lines = normalized.replace(/;\s*/gu, '\n').split('\n')
  const parts: string[] = []
  let current: string[] = []

  for (const rawLine of lines) {
    const line = rawLine.trim().replace(/^[-•*]\s*/u, '').replace(/[«»"'“”„]/gu, '').trim()
    if (!line) {
      if (current.length) {
        parts.push(current.join(' '))
        current = []
      }
      continue
    }
    if (current.length && continuationPattern.test(line)) {
      current.push(line)
    } else {
      if (current.length) parts.push(current.join(' '))
      current = [line]
    }
  }
  if (current.length) parts.push(current.join(' '))

  if (parts.length === 1 && normalized.includes(',')) {
    return normalized
      .split(/,\s+/u)
      .map((part) => part.trim())
      .filter(Boolean)
  }

  const seen = new Set<string>()
  return parts
    .map((part) => stripSentenceSuffix(part))
    .filter((part) => {
      if (!part) return false
      const key = part.toLocaleLowerCase()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
}

function renderLeadDefinition(value: string): string {
  const cleaned = normalizeLogicText(value)
  if (!cleaned) return ''
  const lower = cleaned.toLocaleLowerCase()
  if (lower.startsWith('считай лидом') || lower.startsWith('lead:')) {
    return ensureSentence(cleaned)
  }
  return ensureSentence(`Считай лидом сообщения, где ${cleaned}`)
}

function stripLeadFieldPrefix(value: string): string {
  return value
    .replace(/^считай лидом\s+сообщения,?\s+где\s+/iu, '')
    .replace(/^lead(?:\s+messages?)?:\s*/iu, '')
    .trim()
}

function parseLeadLogicPrompt(prompt: string | null | undefined): LeadLogicSpec {
  const raw = String(prompt ?? '').trim()
  if (!raw) return emptyLeadLogicSpec()

  let body = raw
  for (const tail of [LOGIC_TAIL, ...LEGACY_LOGIC_TAILS]) {
    body = body.replace(new RegExp(`\\s*${escapeRegex(tail)}\\s*$`, 'iu'), '').trim()
  }
  const labels = Object.values(LOGIC_LABEL_ALIASES).flat()
  const tokens = body.split(new RegExp(`(${labels.map(escapeRegex).join('|')})`, 'u'))
  const hasLabels = tokens.length > 1
  const lower = body.toLocaleLowerCase()
  const startsLikeLogic = lower.startsWith('lead:') || lower.startsWith('lead messages') || lower.startsWith('считай лидом')
  if (!hasLabels && !startsLikeLogic) {
    return { ...emptyLeadLogicSpec(), lead: body }
  }

  const spec = {
    ...emptyLeadLogicSpec(),
    structured: true,
    lead: stripLeadFieldPrefix(stripSentenceSuffix(tokens[0] ?? '')),
  }
  for (let i = 1; i < tokens.length; i += 2) {
    const label = tokens[i]
    const rawValue = tokens[i + 1] ?? ''
    const value = stripSentenceSuffix(rawValue)
    if (LOGIC_LABEL_ALIASES.context.some((alias) => alias === label)) spec.context = value
    if (LOGIC_LABEL_ALIASES.targetAuthor.some((alias) => alias === label)) spec.targetAuthor = value
    if (LOGIC_LABEL_ALIASES.avoidRole.some((alias) => alias === label)) spec.avoidRole = value
    if (LOGIC_LABEL_ALIASES.positiveExamples.some((alias) => alias === label)) {
      spec.positiveExamples = splitLogicExamples(rawValue)
    }
    if (LOGIC_LABEL_ALIASES.negativeExamples.some((alias) => alias === label)) {
      spec.negativeExamples = splitLogicExamples(rawValue)
    }
  }
  return spec
}

function renderLeadLogicPrompt(spec: LeadLogicSpec): string {
  const parts: string[] = []
  const lead = renderLeadDefinition(spec.lead)
  if (lead) parts.push(lead)
  if (spec.context.trim()) parts.push(`${LOGIC_LABELS.context} ${ensureSentence(stripSentenceSuffix(spec.context))}`)
  if (spec.targetAuthor.trim()) parts.push(`${LOGIC_LABELS.targetAuthor} ${ensureSentence(stripSentenceSuffix(spec.targetAuthor))}`)
  if (spec.avoidRole.trim()) parts.push(`${LOGIC_LABELS.avoidRole} ${ensureSentence(stripSentenceSuffix(spec.avoidRole))}`)
  const positive = splitLogicExamples(spec.positiveExamples)
  if (positive.length) {
    parts.push(LOGIC_LABELS.positiveExamples)
    positive.forEach((example) => {
      parts.push(`- ${example}`)
    })
  }
  const negative = splitLogicExamples(spec.negativeExamples)
  if (negative.length) {
    parts.push(LOGIC_LABELS.negativeExamples)
    negative.forEach((example) => {
      parts.push(`- ${example}`)
    })
  }
  if (parts.length) parts.push(LOGIC_TAIL)
  return parts
    .map((line) => normalizeLogicText(line))
    .filter(Boolean)
    .join('\n')
}

function updateTextField(spec: LeadLogicSpec, field: LeadLogicTextField, value: string): LeadLogicSpec {
  return { ...spec, structured: true, [field]: value }
}

function updateExampleAt(
  spec: LeadLogicSpec,
  field: LeadLogicExampleField,
  index: number,
  value: string,
): LeadLogicSpec {
  const examples = [...spec[field]]
  examples[index] = value
  return { ...spec, structured: true, [field]: examples }
}

function addExample(spec: LeadLogicSpec, field: LeadLogicExampleField): LeadLogicSpec {
  return { ...spec, structured: true, [field]: [...spec[field], ''] }
}

function removeExampleAt(spec: LeadLogicSpec, field: LeadLogicExampleField, index: number): LeadLogicSpec {
  return { ...spec, structured: true, [field]: spec[field].filter((_, i) => i !== index) }
}

function ExampleListEditor({
  label,
  examples,
  onAdd,
  onChange,
  onRemove,
}: {
  label: string
  examples: string[]
  onAdd: () => void
  onChange: (index: number, value: string) => void
  onRemove: (index: number) => void
}) {
  return (
    <div className="ai-example-editor">
      <div className="ai-example-editor__head">
        <span>{label}</span>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onAdd}>
          Добавить пример
        </button>
      </div>
      {examples.length === 0 ? (
        <div className="ai-example-editor__empty">Примеров пока нет.</div>
      ) : (
        <div className="ai-example-editor__list">
          {examples.map((example, index) => (
            <label key={index} className="ai-example-editor__item">
              <span>Пример {index + 1}</span>
              <textarea
                className="proj-field"
                rows={2}
                value={example}
                onChange={(e) => onChange(index, e.target.value)}
              />
              <button type="button" className="ai-example-editor__remove" onClick={() => onRemove(index)}>
                Удалить
              </button>
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

// camelCase editor spec → snake_case ai_intent_fields payload sent to the API.
function specToFields(spec: LeadLogicSpec): AiLogicFields {
  return {
    lead: spec.lead.trim(),
    context: spec.context.trim(),
    target_author: spec.targetAuthor.trim(),
    avoid_role: spec.avoidRole.trim(),
    positive_examples: splitLogicExamples(spec.positiveExamples),
    negative_examples: splitLogicExamples(spec.negativeExamples),
    structured: true,
    source: 'fields',
  }
}

function fieldsHaveContent(f: AiLogicFields | null | undefined): boolean {
  if (!f) return false
  return Boolean(
    f.lead?.trim() ||
      f.context?.trim() ||
      f.target_author?.trim() ||
      f.avoid_role?.trim() ||
      f.positive_examples?.length ||
      f.negative_examples?.length,
  )
}

// Prefer the stored structured source-of-truth; fall back to parsing the prompt
// text for legacy rows that predate ai_intent_fields.
function specFromDetail(detail: ProjectDetailResponse): LeadLogicSpec {
  const f = detail.ai_intent_fields
  if (fieldsHaveContent(f)) {
    return {
      lead: f.lead ?? '',
      context: f.context ?? '',
      targetAuthor: f.target_author ?? '',
      avoidRole: f.avoid_role ?? '',
      positiveExamples: f.positive_examples ?? [],
      negativeExamples: f.negative_examples ?? [],
      structured: f.structured ?? true,
    }
  }
  return parseLeadLogicPrompt(detail.ai_intent_prompt ?? '')
}

// Break legacy AI prompt text into readable statements:
// split on blank lines / newlines first, then on sentence boundaries, but only
// when the next sentence starts with a capital/quote so abbreviations stay whole.
function splitCriteria(prompt: string): string[] {
  return prompt
    .split(/\n+/)
    .flatMap((line) => line.split(/(?<=[.!?])\s+(?=[«"A-ZА-ЯЁ])/u))
    .map((part) => part.trim())
    .filter(Boolean)
}

function AiLogicOverview({
  detail,
  aiPrompt,
  onEditCriteria,
}: {
  detail: ProjectDetailResponse
  aiPrompt: string
  onEditCriteria: () => void
}) {
  const spec = specFromDetail(detail)
  const sentences = splitCriteria(aiPrompt)
  const structuredRows = [
    { label: 'Считать лидом', value: spec.lead },
    { label: 'Контекст', value: spec.context },
    { label: 'Целевой автор', value: spec.targetAuthor },
    { label: 'Не путать с ролью', value: spec.avoidRole },
  ].filter((row) => row.value.trim())

  return (
    <div className="ai-logic-overview">
      <div className="ai-logic-card">
        <span>Кого ищем</span>
        <p>{detail.raw_input?.trim() || 'Описание проекта пока не задано.'}</p>
      </div>
      <div className="ai-logic-card">
        <div className="ai-logic-card__head">
          <span>Критерии AI</span>
          <button type="button" className="ai-logic-edit" onClick={onEditCriteria}>
            Редактировать
          </button>
        </div>
        {spec.structured && (structuredRows.length > 0 || spec.positiveExamples.length > 0 || spec.negativeExamples.length > 0) ? (
          <div className="ai-logic-structured">
            {structuredRows.map((row) => (
              <div key={row.label} className="ai-logic-row">
                <span>{row.label}</span>
                <p>{row.value}</p>
              </div>
            ))}
            {spec.positiveExamples.length > 0 && (
              <div className="ai-logic-row">
                <span>Подходящие примеры</span>
                <ul className="ai-logic-list">
                  {spec.positiveExamples.map((example) => (
                    <li key={example}>{example}</li>
                  ))}
                </ul>
              </div>
            )}
            {spec.negativeExamples.length > 0 && (
              <div className="ai-logic-row">
                <span>Примеры не-лидов</span>
                <ul className="ai-logic-list">
                  {spec.negativeExamples.map((example) => (
                    <li key={example}>{example}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : sentences.length > 0 ? (
          <ul className="ai-logic-list">
            {sentences.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        ) : (
          <p>
            Критерии AI пока не заданы.{' '}
            <button type="button" className="ai-logic-edit" onClick={onEditCriteria}>
              Добавить
            </button>
          </p>
        )}
      </div>
    </div>
  )
}

function ProjectDrawer({
  projectId,
  projectNumbers,
  onClose,
  onOpenSetup,
  onDuplicated,
}: {
  projectId: number
  projectNumbers: Map<number, number>
  onClose: () => void
  onOpenSetup: () => void
  onDuplicated: (newProjectId: number) => void
}) {
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<DrawerTab>('main')
  const { user } = useAuth()
  const aiAvailable = !!user && user.has_ai

  // Load detail
  const { data: detail, isLoading } = useQuery({
    queryKey: ['project-detail', projectId],
    queryFn: () => api.getProjectDetail(projectId),
  })

  // Main tab state
  const [name, setName] = useState<string>('')
  const [rawInput, setRawInput] = useState<string>('')
  const [mainOk, setMainOk] = useState(false)
  const [mainErr, setMainErr] = useState<string | null>(null)
  const [lifecycleErr, setLifecycleErr] = useState<string | null>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [duplicateConfirmOpen, setDuplicateConfirmOpen] = useState(false)
  const [handoffOpen, setHandoffOpen] = useState(false)
  const [handoffTarget, setHandoffTarget] = useState('')
  const [handoffPreview, setHandoffPreview] = useState<ProjectHandoffResponse | null>(null)
  const [handoffError, setHandoffError] = useState<string | null>(null)

  // AI tab state
  const [aiPrompt, setAiPrompt] = useState<string>('')
  const [aiSpec, setAiSpec] = useState<LeadLogicSpec>(() => emptyLeadLogicSpec())
  const [aiEditMode, setAiEditMode] = useState<AiEditMode>('fields')
  const [aiOk, setAiOk] = useState(false)
  const [aiErr, setAiErr] = useState<string | null>(null)
  const [aiEditing, setAiEditing] = useState(false)

  // Watch users state
  const [watchInput, setWatchInput] = useState('')
  const [watchUsers, setWatchUsers] = useState<string[]>([])
  const [watchOk, setWatchOk] = useState(false)
  const [watchErr, setWatchErr] = useState<string | null>(null)

  // Initialise local state when detail loads
  const [initialised, setInitialised] = useState(false)
  if (detail && !initialised) {
    setName(detail.name ?? '')
    setRawInput(detail.raw_input ?? '')
    setAiPrompt(detail.ai_intent_prompt ?? '')
    setAiSpec(specFromDetail(detail))
    setWatchUsers(detail.watch_usernames ?? [])
    setInitialised(true)
  }

  useEffect(() => {
    setDeleteConfirmOpen(false)
    setDuplicateConfirmOpen(false)
    setHandoffOpen(false)
    setHandoffTarget('')
    setHandoffPreview(null)
    setHandoffError(null)
  }, [projectId])

  function invalidateAll() {
    void queryClient.invalidateQueries({ queryKey: ['projects'] })
    void queryClient.invalidateQueries({ queryKey: ['project-summaries'] })
    void queryClient.invalidateQueries({ queryKey: ['project-healths'] })
    void queryClient.invalidateQueries({ queryKey: ['project-detail', projectId] })
  }

  // Mutations
  const updateMutation = useMutation({
    mutationFn: (body: { name?: string | null; raw_input?: string | null }) =>
      api.updateProject(projectId, body),
    onSuccess: () => {
      setMainOk(true)
      invalidateAll()
    },
    onError: (e) => {
      setMainErr(e instanceof ApiError ? e.message : String(e))
    },
  })

  const activateMutation = useMutation({
    mutationFn: () => api.activateProject(projectId),
    onSuccess: async (updated) => {
      setLifecycleErr(null)
      queryClient.setQueryData(['project-detail', projectId], updated)
      queryClient.setQueryData(['project', projectId], updated)
      queryClient.setQueryData<ProjectResponse[]>(['projects'], (old) =>
        old?.map((project) => (project.id === updated.id ? { ...project, ...updated } : project)),
      )
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['projects'] }),
        queryClient.invalidateQueries({ queryKey: ['project-summaries'] }),
        queryClient.invalidateQueries({ queryKey: ['project-healths'] }),
        queryClient.invalidateQueries({ queryKey: ['project-detail', projectId] }),
      ])
      window.location.reload()
    },
    onError: (e) => {
      setLifecycleErr(e instanceof ApiError ? e.message : String(e))
    },
  })

  const statusMutation = useMutation({
    mutationFn: (status: 'active' | 'archived' | 'draft') =>
      api.setProjectStatus(projectId, status),
    onSuccess: () => {
      setLifecycleErr(null)
      invalidateAll()
    },
    onError: (e) => {
      setLifecycleErr(e instanceof ApiError ? e.message : String(e))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => api.deleteProject(projectId),
    onSuccess: () => {
      setDeleteConfirmOpen(false)
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      void queryClient.invalidateQueries({ queryKey: ['project-summaries'] })
      void queryClient.invalidateQueries({ queryKey: ['project-healths'] })
      onClose()
    },
    onError: (e) => {
      setLifecycleErr(e instanceof ApiError ? e.message : String(e))
    },
  })

  const duplicateMutation = useMutation({
    mutationFn: () => api.duplicateProject(projectId),
    onSuccess: (created) => {
      setDuplicateConfirmOpen(false)
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      void queryClient.invalidateQueries({ queryKey: ['project-summaries'] })
      void queryClient.invalidateQueries({ queryKey: ['project-healths'] })
      onDuplicated(created.id)
    },
    onError: (e) => {
      setLifecycleErr(e instanceof ApiError ? e.message : String(e))
    },
  })

  const handoffPreviewMutation = useMutation({
    mutationFn: () => api.previewProjectHandoff(projectId, handoffTarget.trim()),
    onSuccess: (result) => {
      setHandoffError(null)
      setHandoffPreview(result)
    },
    onError: (error) => {
      setHandoffPreview(null)
      setHandoffError(apiErrorMessage(error, 'Не удалось проверить получателя'))
    },
  })

  const handoffMutation = useMutation({
    mutationFn: () => api.executeProjectHandoff(projectId, handoffTarget.trim()),
    onSuccess: (result) => {
      setHandoffError(null)
      setHandoffPreview(result)
    },
    onError: (error) => {
      setHandoffError(apiErrorMessage(error, 'Не удалось передать проект'))
    },
  })

  const aiMutation = useMutation({
    mutationFn: (vars: { prompt: string; fields?: AiLogicFields }) =>
      api.updateProjectAiPrompt(projectId, vars.prompt, vars.fields),
    onSuccess: (updated) => {
      setAiPrompt(updated.ai_intent_prompt ?? '')
      setAiSpec(specFromDetail(updated))
      setAiEditMode('fields')
      setAiOk(true)
      setAiEditing(false)
      invalidateAll()
    },
    onError: (e) => {
      setAiErr(e instanceof ApiError ? e.message : String(e))
    },
  })

  const watchMutation = useMutation({
    mutationFn: (usernames: string[]) => api.updateProjectWatchUsers(projectId, usernames),
    onSuccess: () => {
      setWatchOk(true)
      invalidateAll()
    },
    onError: (e) => {
      setWatchErr(e instanceof ApiError ? e.message : String(e))
    },
  })

  const kwMutation = useMutation({
    mutationFn: (kws: string[]) => api.updateProjectKeywords(projectId, kws),
    onSuccess: invalidateAll,
  })

  const exclMutation = useMutation({
    mutationFn: (kws: string[]) => api.updateProjectExcludeKeywords(projectId, kws),
    onSuccess: invalidateAll,
  })

  function requestDelete() {
    setLifecycleErr(null)
    setDeleteConfirmOpen(true)
  }

  function confirmDelete() {
    setLifecycleErr(null)
    deleteMutation.mutate()
  }

  function requestDuplicate() {
    setLifecycleErr(null)
    setDuplicateConfirmOpen(true)
  }

  function confirmDuplicate() {
    setLifecycleErr(null)
    duplicateMutation.mutate()
  }

  function addWatchUser(raw: string) {
    const cleaned = raw.replace(/^@/, '').toLowerCase().trim()
    if (!cleaned) return
    if (watchUsers.includes(cleaned)) return
    if (watchUsers.length >= 10) return
    setWatchUsers((prev) => [...prev, cleaned])
    setWatchInput('')
  }

  const lifecyclePending =
    activateMutation.isPending ||
    statusMutation.isPending ||
    deleteMutation.isPending ||
    duplicateMutation.isPending
  const canHandoff =
    user?.telegram_user_id === 6477828562 &&
    detail?.source_management_mode !== 'sensor_managed'
  const renderedAiPrompt = renderLeadLogicPrompt(aiSpec)
  const editableAiPrompt = aiEditMode === 'fields' ? renderedAiPrompt : aiPrompt.trim()
  const aiPromptTooLong = editableAiPrompt.length > AI_PROMPT_MAX_CHARS

  function setAiMode(nextMode: AiEditMode) {
    if (nextMode === aiEditMode) return
    if (nextMode === 'text') {
      setAiPrompt(renderedAiPrompt)
    } else {
      setAiSpec(parseLeadLogicPrompt(aiPrompt))
    }
    setAiEditMode(nextMode)
    setAiOk(false)
    setAiErr(null)
  }

  return (
    <aside className="detail proj-drawer" aria-label="Детали проекта">
      {/* Mobile back */}
      <button className="detail__back-mobile" onClick={onClose} aria-label="Назад">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
        К проектам
      </button>

      {/* Header */}
      <div className="detail__head">
        {/* Keyed remount: React does not reliably patch this lone text node in
            place when the detail query resolves after first paint, leaving the
            header stuck on "Загрузка…". The key flips once detail arrives,
            forcing a fresh node (same quirk handled on the Sources H1). */}
        <span
          key={detail ? `name-${detail.id}` : 'loading'}
          style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink)' }}
        >
          {detail
            ? projectLabel(detail, projectNumbers)
            : isLoading
              ? 'Загрузка…'
              : `Проект #${projectNumbers.get(projectId) ?? projectId}`}
        </span>
        <div className="actions">
          <button className="detail__close" onClick={onClose} aria-label="Закрыть">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M6 6l12 12M18 6l-12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="proj-drawer__tabs">
        {(Object.keys(TAB_LABELS) as DrawerTab[]).map((t) => (
          <button
            key={t}
            type="button"
            className={`proj-drawer__tab${tab === t ? ' is-active' : ''}`}
            onClick={() => setTab(t)}
          >
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      {/* Body */}
      <div className="detail__body" style={{ gap: 16 }}>
        {isLoading && !detail && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--muted)', fontSize: 13 }}>
            <Spinner />
            Загрузка…
          </div>
        )}

        {detail && tab === 'main' && (
          <>
            {/* Name + description */}
            <div style={{ display: 'grid', gap: 10 }}>
              <div className="proj-drawer__field-label">Название</div>
              <input
                type="text"
                className="proj-field"
                value={name}
                placeholder="Название проекта"
                onChange={(e) => { setName(e.target.value); setMainOk(false); setMainErr(null) }}
              />
              <div className="proj-drawer__field-label">Описание</div>
              <textarea
                className="proj-field"
                rows={4}
                value={rawInput}
                placeholder="Описание бизнеса и целевой аудитории…"
                onChange={(e) => { setRawInput(e.target.value); setMainOk(false); setMainErr(null) }}
              />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <InlineFeedback ok={mainOk} err={mainErr} />
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  disabled={updateMutation.isPending}
                  onClick={() => {
                    setMainOk(false)
                    setMainErr(null)
                    updateMutation.mutate({ name: name || null, raw_input: rawInput || null })
                  }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  {updateMutation.isPending && <Spinner />}
                  Сохранить
                </button>
              </div>
            </div>

            {/* Status badge */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div className="proj-drawer__field-label" style={{ margin: 0 }}>Статус:</div>
              <StatusBadge status={detail.status} />
            </div>

            {/* Quick nav to this project's sources */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-start' }}>
              <Link
                to={`/sources?project=${projectId}`}
                className="btn btn--ghost btn--sm"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                Источники проекта →
              </Link>
              <HistoryScanControl projectId={projectId} aiAvailable={aiAvailable} />
            </div>

            {/* Telegram access (read-only — re-binding can break source access) */}
            <div style={{ display: 'grid', gap: 8 }}>
              <div className="proj-drawer__field-label">Телеграм доступ</div>
              <div className="proj-access">
                <span className="proj-access__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
                    <path d="M21.5 4.5 2.5 11.5l6 2.2M21.5 4.5l-3 14.5-6.5-5M21.5 4.5 8.5 15.7m0 0V20l3.5-3.3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <span className="proj-access__body">
                  <span className="proj-access__name">
                    {detail.userbot_display_name || detail.userbot_label || (
                      detail.source_management_mode === 'sensor_managed'
                        ? 'SENSOR аккаунт'
                        : 'Не подключён'
                    )}
                  </span>
                  <span className="proj-access__hint">
                    Аккаунт, через который SENSOR читает источники проекта
                  </span>
                </span>
              </div>
            </div>

            {/* Lifecycle */}
            <div style={{ display: 'grid', gap: 8 }}>
              <div className="proj-drawer__field-label">Управление проектом</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {detail.status === 'draft' && (
                  <button
                    type="button"
                    className="btn btn--primary btn--sm"
                    disabled={lifecyclePending}
                    onClick={onOpenSetup}
                  >
                    Продолжить настройку
                  </button>
                )}
                {detail.status === 'active' && (
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    disabled={lifecyclePending}
                    onClick={() => { setLifecycleErr(null); statusMutation.mutate('archived') }}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    {statusMutation.isPending && <Spinner />}
                    Пауза
                  </button>
                )}
                {detail.status === 'archived' && (
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    disabled={lifecyclePending}
                    onClick={() => { setLifecycleErr(null); activateMutation.mutate() }}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    {activateMutation.isPending && <Spinner />}
                    Возобновить
                  </button>
                )}
                {canHandoff && (
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    disabled={handoffMutation.isPending}
                    onClick={() => {
                      setHandoffOpen((open) => !open)
                      setHandoffError(null)
                    }}
                  >
                    Передать клиенту
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={lifecyclePending}
                  onClick={requestDuplicate}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  {duplicateMutation.isPending && <Spinner />}
                  Дублировать проект
                </button>
                <button
                  type="button"
                  className="btn btn--sm proj-btn--danger"
                  disabled={lifecyclePending}
                  onClick={requestDelete}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  {deleteMutation.isPending && <Spinner />}
                  Удалить проект
                </button>
              </div>
              {handoffOpen && canHandoff && (
                <div className="proj-handoff">
                  <div>
                    <b>Создать клиенту управляемую копию</b>
                    <p>
                      Проект, результаты и источники будут скопированы. Клиент сможет
                      менять критерии и запускать сканирование, но не получит доступ к
                      рабочему юзерботу и не сможет менять источники.
                    </p>
                  </div>
                  <label className="proj-handoff__target">
                    <span>Telegram клиента</span>
                    <input
                      type="text"
                      className="proj-field"
                      value={handoffTarget}
                      placeholder="@username или Telegram ID"
                      disabled={handoffMutation.isPending}
                      onChange={(event) => {
                        setHandoffTarget(event.target.value)
                        setHandoffPreview(null)
                        setHandoffError(null)
                      }}
                    />
                  </label>
                  {handoffPreview && (
                    <div className="proj-handoff__preview">
                      <b>
                        Получатель:{' '}
                        {handoffPreview.target_username
                          ? `@${handoffPreview.target_username}`
                          : handoffPreview.target_user_id}
                      </b>
                      <span>
                        {handoffPreview.counts.sources} источников ·{' '}
                        {handoffPreview.counts.leads} лидов ·{' '}
                        {handoffPreview.counts.audience_accounts} участников аудитории
                      </span>
                      <span>
                        {handoffPreview.counts.history_scans} сканов истории ·{' '}
                        {handoffPreview.counts.monitoring_matches} совпадений ·{' '}
                        {handoffPreview.counts.audience_messages} сообщений аудитории
                      </span>
                      {handoffPreview.target_project_id && (
                        <span className="proj-handoff__success">
                          Готово: проект клиента #{handoffPreview.target_project_id}
                          {handoffPreview.already_completed ? ' уже был создан ранее' : ' создан'}.
                        </span>
                      )}
                    </div>
                  )}
                  {handoffError && (
                    <span className="proj-handoff__error">{handoffError}</span>
                  )}
                  <div className="proj-handoff__actions">
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      disabled={
                        !handoffTarget.trim() ||
                        handoffPreviewMutation.isPending ||
                        handoffMutation.isPending
                      }
                      onClick={() => handoffPreviewMutation.mutate()}
                    >
                      {handoffPreviewMutation.isPending ? 'Проверяю…' : 'Проверить'}
                    </button>
                    {handoffPreview && !handoffPreview.target_project_id && (
                      <button
                        type="button"
                        className="btn btn--primary btn--sm"
                        disabled={handoffMutation.isPending}
                        onClick={() => handoffMutation.mutate()}
                      >
                        {handoffMutation.isPending
                          ? 'Копирую проект…'
                          : 'Подтвердить передачу'}
                      </button>
                    )}
                  </div>
                </div>
              )}
              {duplicateConfirmOpen && (
                <div className="proj-duplicate-confirm" role="dialog">
                  <div>
                    <b>Дублировать «{projectLabel(detail, projectNumbers)}»?</b>
                    <p>
                      Будет создан новый проект со словами, AI-промптом, источниками
                      и настройками. Лиды и результаты сканирования не копируются.
                      Юзербот привязывается заново.
                    </p>
                  </div>
                  <div className="proj-duplicate-confirm__actions">
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      disabled={duplicateMutation.isPending}
                      onClick={() => setDuplicateConfirmOpen(false)}
                    >
                      Отмена
                    </button>
                    <button
                      type="button"
                      className="btn btn--primary btn--sm"
                      disabled={duplicateMutation.isPending}
                      onClick={confirmDuplicate}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    >
                      {duplicateMutation.isPending && <Spinner />}
                      Дублировать
                    </button>
                  </div>
                </div>
              )}
              {deleteConfirmOpen && (
                <div className="proj-delete-confirm" role="alert">
                  <div>
                    <b>Удалить «{projectLabel(detail, projectNumbers)}»?</b>
                    <p>Лиды, настройки и привязки проекта будут удалены. Это действие нельзя отменить.</p>
                  </div>
                  <div className="proj-delete-confirm__actions">
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      disabled={deleteMutation.isPending}
                      onClick={() => setDeleteConfirmOpen(false)}
                    >
                      Отмена
                    </button>
                    <button
                      type="button"
                      className="btn btn--sm proj-btn--danger"
                      disabled={deleteMutation.isPending}
                      onClick={confirmDelete}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    >
                      {deleteMutation.isPending && <Spinner />}
                      Удалить
                    </button>
                  </div>
                </div>
              )}
              {lifecycleErr && (
                <span style={{ fontSize: 12, color: 'var(--danger-ink)', wordBreak: 'break-word' }}>
                  {lifecycleErr}
                </span>
              )}
            </div>
          </>
        )}

        {detail && tab === 'keywords' && (
          <KeywordEditor
            label="Ключевые слова"
            initialKeywords={detail.base_keywords}
            cap={detail.keywords_cap ?? 100}
            onSave={(kws) => kwMutation.mutateAsync(kws).then((updated) => updated.base_keywords)}
            isPending={kwMutation.isPending}
          />
        )}

        {detail && tab === 'exclude' && (
          <KeywordEditor
            label="Слова-исключения"
            initialKeywords={detail.exclude_keywords}
            cap={detail.exclude_keywords_cap ?? 10000}
            onSave={(kws) => exclMutation.mutateAsync(kws).then((updated) => updated.exclude_keywords)}
            isPending={exclMutation.isPending}
          />
        )}

        {detail && tab === 'ai' && !aiEditing && (
          <AiLogicOverview
            detail={detail}
            aiPrompt={aiPrompt}
            onEditCriteria={() => {
              setAiPrompt(detail.ai_intent_prompt ?? '')
              setAiSpec(specFromDetail(detail))
              setAiEditMode('fields')
              setAiOk(false)
              setAiErr(null)
              setAiEditing(true)
            }}
          />
        )}

        {detail && tab === 'ai' && aiEditing && (
          <div className="ai-logic-editor">
            <div className="ai-logic-card">
              <span>Кого ищем</span>
              <p>{detail.raw_input?.trim() || 'Описание проекта пока не задано.'}</p>
            </div>

            <div className="ai-logic-mode" role="tablist" aria-label="AI logic edit mode">
              <button
                type="button"
                className={aiEditMode === 'fields' ? 'is-active' : ''}
                onClick={() => setAiMode('fields')}
              >
                Поля
              </button>
              <button
                type="button"
                className={aiEditMode === 'text' ? 'is-active' : ''}
                onClick={() => setAiMode('text')}
              >
                Текст
              </button>
            </div>

            {aiEditMode === 'fields' ? (
              <>
                <div className="ai-logic-form">
                  <label className="ai-logic-field ai-logic-field--wide">
                    <span>Считать лидом</span>
                    <textarea
                      className="proj-field"
                      rows={3}
                      value={aiSpec.lead}
                      placeholder="автор ищет подрядчика, услугу, поставщика или решение"
                      autoFocus
                      onChange={(e) => {
                        setAiSpec((prev) => updateTextField(prev, 'lead', e.target.value))
                        setAiOk(false)
                        setAiErr(null)
                      }}
                    />
                  </label>
                  <label className="ai-logic-field">
                    <span>Контекст</span>
                    <textarea
                      className="proj-field"
                      rows={3}
                      value={aiSpec.context}
                      placeholder="ниша, география, продукт, тип сделки"
                      onChange={(e) => {
                        setAiSpec((prev) => updateTextField(prev, 'context', e.target.value))
                        setAiOk(false)
                        setAiErr(null)
                      }}
                    />
                  </label>
                  <label className="ai-logic-field">
                    <span>Целевой автор</span>
                    <textarea
                      className="proj-field"
                      rows={3}
                      value={aiSpec.targetAuthor}
                      placeholder="покупатель, клиент, владелец бизнеса, ЛПР"
                      onChange={(e) => {
                        setAiSpec((prev) => updateTextField(prev, 'targetAuthor', e.target.value))
                        setAiOk(false)
                        setAiErr(null)
                      }}
                    />
                  </label>
                  <label className="ai-logic-field ai-logic-field--wide">
                    <span>Не путать с ролью</span>
                    <textarea
                      className="proj-field"
                      rows={2}
                      value={aiSpec.avoidRole}
                      placeholder="подрядчик, продавец, кандидат, рекламодатель, тот кто ищет сотрудника"
                      onChange={(e) => {
                        setAiSpec((prev) => updateTextField(prev, 'avoidRole', e.target.value))
                        setAiOk(false)
                        setAiErr(null)
                      }}
                    />
                  </label>
                  <div className="ai-logic-field">
                    <ExampleListEditor
                      label="Подходящие примеры"
                      examples={aiSpec.positiveExamples}
                      onAdd={() => {
                        setAiSpec((prev) => addExample(prev, 'positiveExamples'))
                        setAiOk(false)
                        setAiErr(null)
                      }}
                      onChange={(index, value) => {
                        setAiSpec((prev) => updateExampleAt(prev, 'positiveExamples', index, value))
                        setAiOk(false)
                        setAiErr(null)
                      }}
                      onRemove={(index) => {
                        setAiSpec((prev) => removeExampleAt(prev, 'positiveExamples', index))
                        setAiOk(false)
                        setAiErr(null)
                      }}
                    />
                  </div>
                  <div className="ai-logic-field">
                    <ExampleListEditor
                      label="Примеры не-лидов"
                      examples={aiSpec.negativeExamples}
                      onAdd={() => {
                        setAiSpec((prev) => addExample(prev, 'negativeExamples'))
                        setAiOk(false)
                        setAiErr(null)
                      }}
                      onChange={(index, value) => {
                        setAiSpec((prev) => updateExampleAt(prev, 'negativeExamples', index, value))
                        setAiOk(false)
                        setAiErr(null)
                      }}
                      onRemove={(index) => {
                        setAiSpec((prev) => removeExampleAt(prev, 'negativeExamples', index))
                        setAiOk(false)
                        setAiErr(null)
                      }}
                    />
                  </div>
                </div>

                <details className="ai-logic-preview">
                  <summary>Итоговая инструкция для AI</summary>
                  <pre>{renderedAiPrompt || 'AI-логика пока не задана.'}</pre>
                </details>
              </>
            ) : (
              <label className="ai-logic-field ai-logic-field--wide">
                <span>Инструкция для AI</span>
                <textarea
                  className="proj-field ai-logic-textarea"
                  rows={10}
                  value={aiPrompt}
                  placeholder="Опишите простыми словами, кто считается лидом, кто нет и какие нюансы важны."
                  autoFocus
                  maxLength={AI_PROMPT_MAX_CHARS}
                  onChange={(e) => {
                    setAiPrompt(e.target.value)
                    setAiOk(false)
                    setAiErr(null)
                  }}
                />
              </label>
            )}

            <div className="ai-logic-actions">
              <span className={aiPromptTooLong ? 'is-danger' : ''}>
                {editableAiPrompt.length}/{AI_PROMPT_MAX_CHARS}
              </span>
              <div>
                <InlineFeedback ok={aiOk} err={aiErr} />
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={aiMutation.isPending}
                  onClick={() => {
                    setAiPrompt(detail.ai_intent_prompt ?? '')
                    setAiSpec(specFromDetail(detail))
                    setAiEditMode('fields')
                    setAiOk(false)
                    setAiErr(null)
                    setAiEditing(false)
                  }}
                >
                  Отмена
                </button>
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  disabled={aiMutation.isPending || aiPromptTooLong}
                  onClick={() => {
                    setAiOk(false)
                    setAiErr(null)
                    aiMutation.mutate(
                      aiEditMode === 'fields'
                        ? { prompt: editableAiPrompt, fields: specToFields(aiSpec) }
                        : { prompt: editableAiPrompt },
                    )
                  }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  {aiMutation.isPending && <Spinner />}
                  Сохранить
                </button>
              </div>
            </div>
          </div>
        )}

        {detail && tab === 'watch' && (
          <div style={{ display: 'grid', gap: 10 }}>
            <div className="proj-drawer__field-label">Отслеживаемые пользователи</div>
            <p style={{ margin: 0, fontSize: 12, color: 'var(--muted)' }}>
              SENSOR будет отмечать сообщения этих пользователей в источниках проекта.
            </p>

            {/* Chips */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {watchUsers.map((u) => (
                <span key={u} className="proj-kw-chip">
                  @{u}
                  <button
                    type="button"
                    aria-label={`Удалить @${u}`}
                    onClick={() => setWatchUsers((prev) => prev.filter((x) => x !== u))}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0 0 0 4px',
                      color: 'var(--muted)',
                      fontSize: 12,
                      lineHeight: 1,
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
              {watchUsers.length === 0 && (
                <span style={{ fontSize: 12, color: 'var(--muted-2)' }}>Нет пользователей</span>
              )}
            </div>

            {/* Input */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="text"
                className="proj-field"
                style={{ flex: 1 }}
                placeholder="@username"
                value={watchInput}
                disabled={watchUsers.length >= 10}
                onChange={(e) => setWatchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addWatchUser(watchInput)
                  }
                }}
              />
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={!watchInput.trim() || watchUsers.length >= 10}
                onClick={() => addWatchUser(watchInput)}
              >
                Добавить
              </button>
            </div>

            <div
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
            >
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  color: watchUsers.length >= 10 ? 'var(--danger-ink)' : 'var(--muted)',
                }}
              >
                {watchUsers.length}/10
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <InlineFeedback ok={watchOk} err={watchErr} />
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  disabled={watchMutation.isPending}
                  onClick={() => {
                    setWatchOk(false)
                    setWatchErr(null)
                    watchMutation.mutate(watchUsers)
                  }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  {watchMutation.isPending && <Spinner />}
                  Сохранить
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </aside>
  )
}

// ---------------------------------------------------------------------------
// CreateProjectForm
// ---------------------------------------------------------------------------

function CreateProjectForm({
  onCreated,
  onCancel,
}: {
  onCreated: (project: ProjectDetailResponse) => void
  onCancel: () => void
}) {
  const queryClient = useQueryClient()
  const [description, setDescription] = useState('')
  const [err, setErr] = useState<string | null>(null)

  const createMutation = useMutation({
    mutationFn: (desc: string) => api.draftProjectFromDescription(desc),
    onSuccess: (data: ProjectDetailResponse) => {
      queryClient.setQueryData(['project-detail', data.id], data)
      queryClient.setQueryData(['project', data.id], data)
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      onCreated(data)
    },
    onError: (e) => {
      setErr(e instanceof ApiError ? e.message : String(e))
    },
  })

  function handleSubmit() {
    if (!description.trim()) return
    setErr(null)
    createMutation.mutate(description.trim())
  }

  return (
    <div
      style={{
        borderRadius: 'var(--r-lg)',
        border: '1px solid var(--line)',
        background: 'var(--surface)',
        padding: '20px 22px',
        display: 'grid',
        gap: 12,
        marginBottom: 20,
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink)' }}>Новый проект</div>
      <textarea
        className="proj-field"
        rows={4}
        placeholder="Опишите ваш бизнес и кого вы ищете…"
        value={description}
        disabled={createMutation.isPending}
        onChange={(e) => { setDescription(e.target.value); setErr(null) }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmit()
        }}
      />
      {err && (
        <span style={{ fontSize: 12, color: 'var(--danger-ink)', wordBreak: 'break-word' }}>
          {err}
        </span>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          disabled={createMutation.isPending}
          onClick={onCancel}
        >
          Отмена
        </button>
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={createMutation.isPending || !description.trim()}
          onClick={handleSubmit}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
        >
          {createMutation.isPending ? (
            <>
              <Spinner />
              Генерация…
            </>
          ) : (
            'Создать'
          )}
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// ProjectSetupWizard
// ---------------------------------------------------------------------------

type SetupStep = 'review' | 'access' | 'sources' | 'launch'

const SETUP_STEPS: { id: SetupStep; title: string; caption: string }[] = [
  { id: 'review', title: 'AI-черновик', caption: 'Проверить кого ищем' },
  { id: 'access', title: 'Telegram-доступ', caption: 'Выбрать аккаунт' },
  { id: 'sources', title: 'Источники', caption: 'Добавить чаты' },
  { id: 'launch', title: 'Запуск', caption: 'Включить мониторинг' },
]

const PROJECT_SETUP_REVIEWED_KEY = 'sensor.projectSetup.reviewed'

function setupReviewedKey(projectId: number): string {
  return `${PROJECT_SETUP_REVIEWED_KEY}.${projectId}`
}

function projectHasUserbot(detail: ProjectDetailResponse | undefined): boolean {
  return Boolean(
    detail?.userbot_profile_id != null ||
      detail?.resolved_user_session_id != null ||
      detail?.user_session_id != null,
  )
}

function sourceTitle(source: SourceStateResponse): string {
  return source.group_title || source.username || source.source_url || `ID ${source.chat_id}`
}

function ProjectSetupWizard({
  projectId,
  projectNumbers,
  onClose,
  onActivated,
}: {
  projectId: number
  projectNumbers: Map<number, number>
  onClose: () => void
  onActivated: () => void
}) {
  const queryClient = useQueryClient()
  const [reviewAccepted, setReviewAccepted] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.localStorage.getItem(setupReviewedKey(projectId)) === '1'
  })
  const [step, setStep] = useState<SetupStep>(reviewAccepted ? 'access' : 'review')
  const [connectUserbotOpen, setConnectUserbotOpen] = useState(false)
  const [boundUserbotId, setBoundUserbotId] = useState<number | null>(null)
  const [selectedFolderId, setSelectedFolderId] = useState<number | undefined>(undefined)
  const [selectedChatIds, setSelectedChatIds] = useState<Set<number>>(new Set())
  const [sourceSearch, setSourceSearch] = useState('')
  const [manualInput, setManualInput] = useState('')
  const [manualMsg, setManualMsg] = useState<{ text: string; error: boolean } | null>(null)
  const [importResult, setImportResult] = useState<string | null>(null)
  const [lastImportedCount, setLastImportedCount] = useState<number | null>(null)
  const [lifecycleErr, setLifecycleErr] = useState<string | null>(null)

  const [aiPrompt, setAiPrompt] = useState('')
  const [aiSpec, setAiSpec] = useState<LeadLogicSpec>(() => emptyLeadLogicSpec())
  const [aiEditMode, setAiEditMode] = useState<AiEditMode>('fields')
  const [aiEditing, setAiEditing] = useState(false)
  const [aiOk, setAiOk] = useState(false)
  const [aiErr, setAiErr] = useState<string | null>(null)

  const detailQuery = useQuery({
    queryKey: ['project-detail', projectId],
    queryFn: () => api.getProjectDetail(projectId),
  })
  const detail = detailQuery.data

  const sourcesQuery = useQuery({
    queryKey: ['sources', projectId],
    queryFn: () => api.getSources(projectId),
    enabled: !!detail,
  })
  const sources = sourcesQuery.data?.items ?? []
  const activeSources = sources.filter((source) => !source.removed_at)
  const sourceCount = activeSources.length
  const hasUserbot = projectHasUserbot(detail) || boundUserbotId != null
  const hasSources = sourceCount > 0

  const userbotsQuery = useQuery({
    queryKey: ['me', 'userbots'],
    queryFn: () => api.getUserbots(),
  })
  const userbots = userbotsQuery.data?.userbots ?? []

  useEffect(() => {
    setReviewAccepted(window.localStorage.getItem(setupReviewedKey(projectId)) === '1')
    setStep(window.localStorage.getItem(setupReviewedKey(projectId)) === '1' ? 'access' : 'review')
    setBoundUserbotId(null)
  }, [projectId])

  useEffect(() => {
    if (!detail) return
    setAiPrompt(detail.ai_intent_prompt ?? '')
    setAiSpec(specFromDetail(detail))
    setAiEditMode('fields')
    setAiEditing(false)
    setAiOk(false)
    setAiErr(null)
  }, [detail?.id])

  useEffect(() => {
    const maxAllowed = !reviewAccepted ? 0 : !hasUserbot ? 1 : !hasSources ? 2 : 3
    const currentIndex = SETUP_STEPS.findIndex((item) => item.id === step)
    if (currentIndex > maxAllowed) {
      setStep(SETUP_STEPS[maxAllowed].id)
    }
  }, [reviewAccepted, hasUserbot, hasSources, step])

  function invalidateProjectSetup() {
    void queryClient.invalidateQueries({ queryKey: ['projects'] })
    void queryClient.invalidateQueries({ queryKey: ['project-detail', projectId] })
    void queryClient.invalidateQueries({ queryKey: ['project-summaries'] })
    void queryClient.invalidateQueries({ queryKey: ['project-healths'] })
    void queryClient.invalidateQueries({ queryKey: ['sources', projectId] })
  }

  async function refetchProjectSetup() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['projects'] }),
      queryClient.invalidateQueries({ queryKey: ['project-detail', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['project', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['project-summaries'] }),
      queryClient.invalidateQueries({ queryKey: ['project-healths'] }),
      queryClient.invalidateQueries({ queryKey: ['sources', projectId] }),
    ])
  }

  function setProjectCaches(updated: ProjectDetailResponse) {
    queryClient.setQueryData(['project-detail', projectId], updated)
    queryClient.setQueryData(['project', projectId], updated)
    queryClient.setQueryData<ProjectResponse[]>(['projects'], (old) =>
      old?.map((project) => (project.id === updated.id ? { ...project, ...updated } : project)),
    )
  }

  function allowedStepIndex(): number {
    if (!reviewAccepted) return 0
    if (!hasUserbot) return 1
    if (!hasSources) return 2
    return 3
  }

  function goNext() {
    if (step === 'review') {
      window.localStorage.setItem(setupReviewedKey(projectId), '1')
      setReviewAccepted(true)
      setStep(hasUserbot ? (hasSources ? 'launch' : 'sources') : 'access')
      return
    }
    if (step === 'access' && hasUserbot) {
      setStep(hasSources ? 'launch' : 'sources')
      return
    }
    if (step === 'sources' && hasSources) {
      setStep('launch')
    }
  }

  const aiMutation = useMutation({
    mutationFn: (vars: { prompt: string; fields?: AiLogicFields }) =>
      api.updateProjectAiPrompt(projectId, vars.prompt, vars.fields),
    onSuccess: (updated) => {
      setAiPrompt(updated.ai_intent_prompt ?? '')
      setAiSpec(specFromDetail(updated))
      setAiEditMode('fields')
      setAiOk(true)
      setAiEditing(false)
      invalidateProjectSetup()
    },
    onError: (e) => setAiErr(apiErrorMessage(e, 'Не удалось сохранить AI-логику')),
  })

  const kwMutation = useMutation({
    mutationFn: (kws: string[]) => api.updateProjectKeywords(projectId, kws),
    onSuccess: invalidateProjectSetup,
  })

  const exclMutation = useMutation({
    mutationFn: (kws: string[]) => api.updateProjectExcludeKeywords(projectId, kws),
    onSuccess: invalidateProjectSetup,
  })

  const bindMutation = useMutation({
    mutationFn: (userbot: Userbot) => api.bindProjectUserbot(projectId, userbot.id),
    onSuccess: (updated, userbot) => {
      setBoundUserbotId(userbot.id)
      setLifecycleErr(null)
      setProjectCaches(updated)
      invalidateProjectSetup()
      setStep(hasSources ? 'launch' : 'sources')
    },
    onError: (e) => setLifecycleErr(apiErrorMessage(e, 'Не удалось привязать Telegram-доступ')),
  })

  const manualAddMutation = useMutation({
    mutationFn: (input: string) => api.addManualSource(projectId, input),
    onSuccess: async (result) => {
      setManualInput('')
      setManualMsg({
        text: `Добавлено: ${result.added}, уже были: ${result.skipped_existing}, сверх лимита: ${result.skipped_by_limit}${result.skipped_invalid ? `, не найдены: ${result.skipped_invalid}` : ''}`,
        error: false,
      })
      invalidateProjectSetup()
      await queryClient.invalidateQueries({ queryKey: ['import-candidates', projectId] })
      const refreshedSources = await api.getSources(projectId)
      queryClient.setQueryData(['sources', projectId], refreshedSources)
    },
    onError: (err) => setManualMsg({ text: apiErrorMessage(err, 'Ошибка добавления'), error: true }),
  })

  const refreshImportMutation = useMutation({
    mutationFn: () => api.refreshImport(projectId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['import-folders', projectId] }),
  })

  const foldersQuery = useQuery({
    queryKey: ['import-folders', projectId],
    queryFn: () => api.getImportFolders(projectId),
    enabled: step === 'sources' && hasUserbot,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      if (status === 'requested' || status === undefined) return 2000
      return false
    },
  })

  const importStatus = foldersQuery.data?.status ?? 'none'
  const folders: ImportFolder[] = foldersQuery.data?.folders ?? []

  const candidatesQuery = useQuery({
    queryKey: ['import-candidates', projectId, selectedFolderId],
    queryFn: () => api.getImportCandidates(projectId, selectedFolderId),
    enabled: step === 'sources' && hasUserbot && importStatus === 'ready',
  })
  const candidates: ImportCandidate[] = candidatesQuery.data?.candidates ?? []
  const normalizedSourceSearch = sourceSearch.trim().toLowerCase()
  const visibleCandidates = normalizedSourceSearch
    ? candidates.filter((candidate) => {
        const title = String(candidate.title ?? '').toLowerCase()
        const username = String(candidate.username ?? '').toLowerCase()
        return title.includes(normalizedSourceSearch) || username.includes(normalizedSourceSearch)
      })
    : candidates
  const allAvailableCandidates = candidates.filter((candidate) => !candidate.already_added)
  const availableCandidates = visibleCandidates.filter((candidate) => !candidate.already_added)
  const selectedImportIds = Array.from(selectedChatIds).filter((chatId) =>
    allAvailableCandidates.some((candidate) => candidate.chat_id === chatId),
  )
  const allSelected =
    availableCandidates.length > 0 &&
    availableCandidates.every((candidate) => selectedChatIds.has(candidate.chat_id))

  const applyImportMutation = useMutation({
    mutationFn: (chatIds: number[]) => api.applyImport(projectId, chatIds),
    onSuccess: async (result, chatIds) => {
      setSelectedChatIds(new Set())
      setLastImportedCount(chatIds.length)
      setImportResult(
        `Добавлено: ${result.added}, уже были: ${result.skipped_existing}, сверх лимита: ${result.skipped_by_limit}`,
      )
      invalidateProjectSetup()
      await queryClient.invalidateQueries({ queryKey: ['import-candidates', projectId] })
      const refreshedSources = await api.getSources(projectId)
      queryClient.setQueryData(['sources', projectId], refreshedSources)
    },
  })

  const removeSourceMutation = useMutation({
    mutationFn: (sourceStateId: number) => api.removeSource(projectId, sourceStateId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['import-candidates', projectId] })
      const refreshedSources = await api.getSources(projectId)
      queryClient.setQueryData(['sources', projectId], refreshedSources)
      invalidateProjectSetup()
    },
  })

  const activateMutation = useMutation({
    mutationFn: () => api.activateProject(projectId),
    onSuccess: async (updated) => {
      setLifecycleErr(null)
      setProjectCaches(updated)
      const [freshDetail, freshSources] = await Promise.all([
        api.getProjectDetail(projectId),
        api.getSources(projectId),
      ])
      setProjectCaches(freshDetail)
      queryClient.setQueryData(['sources', projectId], freshSources)
      await refetchProjectSetup()
      onActivated()
      window.location.reload()
    },
    onError: (e) => setLifecycleErr(apiErrorMessage(e, 'Не удалось запустить мониторинг')),
  })

  function setAiMode(nextMode: AiEditMode) {
    if (nextMode === aiEditMode) return
    if (nextMode === 'text') {
      setAiPrompt(renderLeadLogicPrompt(aiSpec))
    } else {
      setAiSpec(parseLeadLogicPrompt(aiPrompt))
    }
    setAiEditMode(nextMode)
    setAiOk(false)
    setAiErr(null)
  }

  function toggleCandidate(chatId: number) {
    setLastImportedCount(null)
    setImportResult(null)
    setSelectedChatIds((prev) => {
      const next = new Set(prev)
      if (next.has(chatId)) next.delete(chatId)
      else next.add(chatId)
      return next
    })
  }

  function toggleAllCandidates() {
    setLastImportedCount(null)
    setImportResult(null)
    if (allSelected) {
      setSelectedChatIds((prev) => {
        const next = new Set(prev)
        availableCandidates.forEach((candidate) => next.delete(candidate.chat_id))
        return next
      })
    } else {
      setSelectedChatIds((prev) => {
        const next = new Set(prev)
        availableCandidates.forEach((candidate) => next.add(candidate.chat_id))
        return next
      })
    }
  }

  function chooseFolder(folderId: number | undefined) {
    setSelectedFolderId(folderId)
    setSelectedChatIds(new Set())
    setImportResult(null)
    setLastImportedCount(null)
  }

  const renderedAiPrompt = renderLeadLogicPrompt(aiSpec)
  const editableAiPrompt = aiEditMode === 'fields' ? renderedAiPrompt : aiPrompt.trim()
  const aiPromptTooLong = editableAiPrompt.length > AI_PROMPT_MAX_CHARS
  const stepIndex = SETUP_STEPS.findIndex((item) => item.id === step)
  const maxStepIndex = allowedStepIndex()

  const title = detail ? projectLabel(detail, projectNumbers) : `Проект #${projectId}`

  return (
    <div className="setup-shell" role="dialog" aria-modal="true" aria-label="Настройка проекта">
      <div className="setup-frame">
        <header className="setup-head">
          <div>
            <span>Настройка проекта</span>
            <h1>{title}</h1>
          </div>
          <button type="button" className="setup-close" onClick={onClose} aria-label="Закрыть">
            x
          </button>
        </header>

        <div className="setup-body">
          <aside className="setup-steps" aria-label="Шаги настройки">
            {SETUP_STEPS.map((item, index) => {
              const isDone =
                (item.id === 'review' && reviewAccepted) ||
                (item.id === 'access' && hasUserbot) ||
                (item.id === 'sources' && hasSources) ||
                (item.id === 'launch' && detail?.status === 'active')
              const isLocked = index > maxStepIndex
              return (
                <button
                  key={item.id}
                  type="button"
                  className={`setup-step${step === item.id ? ' is-active' : ''}${isDone ? ' is-done' : ''}`}
                  disabled={isLocked}
                  onClick={() => !isLocked && setStep(item.id)}
                >
                  <i>{isDone ? '✓' : index + 1}</i>
                  <span>
                    <b>{item.title}</b>
                    <small>{item.caption}</small>
                  </span>
                </button>
              )
            })}
          </aside>

          <main className="setup-main">
            {(detailQuery.isLoading || !detail) && (
              <div className="setup-loading">
                <Spinner />
                Загрузка проекта...
              </div>
            )}

            {detail && step === 'review' && (
              <section className="setup-panel">
                <div className="setup-panel__head">
                  <span>Шаг {stepIndex + 1} из {SETUP_STEPS.length}</span>
                  <h2>Проверьте, как SENSOR понял задачу</h2>
                  <p>Перед запуском важно подтвердить критерии: дальше они будут управлять AI-оценкой лидов.</p>
                </div>

                {!aiEditing ? (
                  <>
                    <AiLogicOverview
                      detail={detail}
                      aiPrompt={detail.ai_intent_prompt ?? ''}
                      onEditCriteria={() => {
                        setAiPrompt(detail.ai_intent_prompt ?? '')
                        setAiSpec(specFromDetail(detail))
                        setAiEditMode('fields')
                        setAiEditing(true)
                        setAiOk(false)
                        setAiErr(null)
                      }}
                    />
                    <div className="setup-review-editors">
                      <details>
                        <summary>Ключевые слова</summary>
                        <KeywordEditor
                          label="Ключевые слова"
                          initialKeywords={detail.base_keywords}
                          cap={detail.keywords_cap ?? 100}
                          onSave={(kws) => kwMutation.mutateAsync(kws).then((updated) => updated.base_keywords)}
                          isPending={kwMutation.isPending}
                        />
                      </details>
                      <details>
                        <summary>Исключающие слова</summary>
                        <KeywordEditor
                          label="Слова-исключения"
                          initialKeywords={detail.exclude_keywords}
                          cap={detail.exclude_keywords_cap ?? 10000}
                          onSave={(kws) => exclMutation.mutateAsync(kws).then((updated) => updated.exclude_keywords)}
                          isPending={exclMutation.isPending}
                        />
                      </details>
                    </div>
                  </>
                ) : (
                  <div className="ai-logic-editor">
                    <div className="ai-logic-mode" role="tablist" aria-label="AI logic edit mode">
                      <button
                        type="button"
                        className={aiEditMode === 'fields' ? 'is-active' : ''}
                        onClick={() => setAiMode('fields')}
                      >
                        Поля
                      </button>
                      <button
                        type="button"
                        className={aiEditMode === 'text' ? 'is-active' : ''}
                        onClick={() => setAiMode('text')}
                      >
                        Текст
                      </button>
                    </div>

                    {aiEditMode === 'fields' ? (
                      <>
                        <div className="ai-logic-form">
                          <label className="ai-logic-field ai-logic-field--wide">
                            <span>Считать лидом</span>
                            <textarea
                              className="proj-field"
                              rows={3}
                              value={aiSpec.lead}
                              onChange={(e) => {
                                setAiSpec((prev) => updateTextField(prev, 'lead', e.target.value))
                                setAiOk(false)
                                setAiErr(null)
                              }}
                            />
                          </label>
                          <label className="ai-logic-field">
                            <span>Контекст</span>
                            <textarea
                              className="proj-field"
                              rows={3}
                              value={aiSpec.context}
                              onChange={(e) => {
                                setAiSpec((prev) => updateTextField(prev, 'context', e.target.value))
                                setAiOk(false)
                                setAiErr(null)
                              }}
                            />
                          </label>
                          <label className="ai-logic-field">
                            <span>Целевой автор</span>
                            <textarea
                              className="proj-field"
                              rows={3}
                              value={aiSpec.targetAuthor}
                              onChange={(e) => {
                                setAiSpec((prev) => updateTextField(prev, 'targetAuthor', e.target.value))
                                setAiOk(false)
                                setAiErr(null)
                              }}
                            />
                          </label>
                          <label className="ai-logic-field ai-logic-field--wide">
                            <span>Не путать с ролью</span>
                            <textarea
                              className="proj-field"
                              rows={2}
                              value={aiSpec.avoidRole}
                              onChange={(e) => {
                                setAiSpec((prev) => updateTextField(prev, 'avoidRole', e.target.value))
                                setAiOk(false)
                                setAiErr(null)
                              }}
                            />
                          </label>
                          <div className="ai-logic-field">
                            <ExampleListEditor
                              label="Подходящие примеры"
                              examples={aiSpec.positiveExamples}
                              onAdd={() => {
                                setAiSpec((prev) => addExample(prev, 'positiveExamples'))
                                setAiOk(false)
                                setAiErr(null)
                              }}
                              onChange={(index, value) => {
                                setAiSpec((prev) => updateExampleAt(prev, 'positiveExamples', index, value))
                                setAiOk(false)
                                setAiErr(null)
                              }}
                              onRemove={(index) => {
                                setAiSpec((prev) => removeExampleAt(prev, 'positiveExamples', index))
                                setAiOk(false)
                                setAiErr(null)
                              }}
                            />
                          </div>
                          <div className="ai-logic-field">
                            <ExampleListEditor
                              label="Примеры не-лидов"
                              examples={aiSpec.negativeExamples}
                              onAdd={() => {
                                setAiSpec((prev) => addExample(prev, 'negativeExamples'))
                                setAiOk(false)
                                setAiErr(null)
                              }}
                              onChange={(index, value) => {
                                setAiSpec((prev) => updateExampleAt(prev, 'negativeExamples', index, value))
                                setAiOk(false)
                                setAiErr(null)
                              }}
                              onRemove={(index) => {
                                setAiSpec((prev) => removeExampleAt(prev, 'negativeExamples', index))
                                setAiOk(false)
                                setAiErr(null)
                              }}
                            />
                          </div>
                        </div>
                        <details className="ai-logic-preview">
                          <summary>Итоговая инструкция для AI</summary>
                          <pre>{renderedAiPrompt || 'AI-логика пока не задана.'}</pre>
                        </details>
                      </>
                    ) : (
                      <label className="ai-logic-field ai-logic-field--wide">
                        <span>Инструкция для AI</span>
                        <textarea
                          className="proj-field ai-logic-textarea"
                          rows={10}
                          value={aiPrompt}
                          maxLength={AI_PROMPT_MAX_CHARS}
                          onChange={(e) => {
                            setAiPrompt(e.target.value)
                            setAiOk(false)
                            setAiErr(null)
                          }}
                        />
                      </label>
                    )}

                    <div className="ai-logic-actions">
                      <span className={aiPromptTooLong ? 'is-danger' : ''}>
                        {editableAiPrompt.length}/{AI_PROMPT_MAX_CHARS}
                      </span>
                      <div>
                        <InlineFeedback ok={aiOk} err={aiErr} />
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          disabled={aiMutation.isPending}
                          onClick={() => setAiEditing(false)}
                        >
                          Отмена
                        </button>
                        <button
                          type="button"
                          className="btn btn--primary btn--sm"
                          disabled={aiMutation.isPending || aiPromptTooLong}
                          onClick={() => {
                            setAiOk(false)
                            setAiErr(null)
                            aiMutation.mutate(
                              aiEditMode === 'fields'
                                ? { prompt: editableAiPrompt, fields: specToFields(aiSpec) }
                                : { prompt: editableAiPrompt },
                            )
                          }}
                        >
                          Сохранить
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </section>
            )}

            {detail && step === 'access' && (
              <section className="setup-panel">
                <div className="setup-panel__head">
                  <span>Шаг {stepIndex + 1} из {SETUP_STEPS.length}</span>
                  <h2>Выберите Telegram-доступ</h2>
                  <p>Проект будет читать источники через выбранный аккаунт. Если доступов несколько, выберите нужный явно.</p>
                </div>

                {hasUserbot && (
                  <div className="setup-note setup-note--ok">
                    Сейчас выбран:{' '}
                    {detail.userbot_display_name ||
                      detail.userbot_label ||
                      userbots.find((userbot) => userbot.id === boundUserbotId)?.label ||
                      'Telegram-доступ проекта'}
                  </div>
                )}

                <div className="setup-userbots">
                  {userbotsQuery.isLoading && (
                    <div className="setup-loading">
                      <Spinner />
                      Загружаем доступы...
                    </div>
                  )}
                  {userbots.map((userbot) => {
                    const selected =
                      boundUserbotId === userbot.id ||
                      detail.resolved_user_session_id === userbot.id ||
                      detail.user_session_id === userbot.id
                    return (
                      <button
                        key={userbot.id}
                        type="button"
                        className={`setup-userbot${selected ? ' is-selected' : ''}`}
                        disabled={bindMutation.isPending}
                        onClick={() => {
                          setLifecycleErr(null)
                          bindMutation.mutate(userbot)
                        }}
                      >
                        <span>
                          <b>{userbot.label}</b>
                          <small>{userbot.telegram_user_id ? `ID ${userbot.telegram_user_id}` : 'Telegram аккаунт'}</small>
                        </span>
                        <i>{selected ? 'Выбран' : 'Выбрать'}</i>
                      </button>
                    )
                  })}
                  {!userbotsQuery.isLoading && userbots.length === 0 && (
                    <div className="setup-empty">
                      Telegram-доступов пока нет. Подключите аккаунт по QR-коду, затем выберите его для проекта.
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => setConnectUserbotOpen(true)}
                >
                  Подключить новый Telegram-доступ
                </button>

                {lifecycleErr && <span className="setup-error">{lifecycleErr}</span>}
              </section>
            )}

            {detail && step === 'sources' && (
              <section className="setup-panel setup-panel--sources">
                <div className="setup-panel__head">
                  <span>Шаг {stepIndex + 1} из {SETUP_STEPS.length}</span>
                  <h2>Добавьте источники спроса</h2>
                  <p>Выберите чаты и каналы, где ваша аудитория задаёт вопросы или ищет решения. Минимум один источник.</p>
                </div>

                <div className="setup-source-grid">
                  <div className="setup-source-card">
                    <h3>Добавить вручную</h3>
                    <div className="setup-manual">
                      <textarea
                        className="proj-field"
                        value={manualInput}
                        placeholder={'@username, t.me/... или t.me/чат/тема\nМожно вставить несколько ссылок: по одной в строке или через запятую'}
                        onChange={(e) => {
                          setManualInput(e.target.value)
                          setManualMsg(null)
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && manualInput.trim()) {
                            manualAddMutation.mutate(manualInput.trim())
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        disabled={manualAddMutation.isPending || !manualInput.trim()}
                        onClick={() => manualInput.trim() && manualAddMutation.mutate(manualInput.trim())}
                      >
                        {manualAddMutation.isPending ? '...' : 'Добавить'}
                      </button>
                    </div>
                    {manualMsg && (
                      <span className={manualMsg.error ? 'setup-error' : 'setup-ok'}>{manualMsg.text}</span>
                    )}
                  </div>

                  <div className="setup-source-card">
                    <div className="setup-source-card__head">
                      <h3>Импорт из Telegram</h3>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        disabled={refreshImportMutation.isPending}
                        onClick={() => refreshImportMutation.mutate()}
                      >
                        Обновить
                      </button>
                    </div>

                    {importStatus === 'requested' && (
                      <div className="setup-loading">
                        <Spinner />
                        Синхронизируем диалоги Telegram...
                      </div>
                    )}

                    {importStatus === 'none' && (
                      <div className="setup-note">
                        Диалоги ещё не синхронизированы. Нажмите «Обновить», чтобы загрузить чаты этого Telegram-доступа.
                      </div>
                    )}

                    {importStatus === 'failed' && (
                      <div className="setup-note">
                        Не удалось синхронизировать диалоги. Попробуйте обновить список.
                      </div>
                    )}

                    {importStatus === 'no_userbot' && (
                      <div className="setup-note">
                        Для импорта нужен выбранный Telegram-доступ.
                      </div>
                    )}

                    {importStatus === 'ready' && (
                      <>
                        <div className="setup-folders">
                          <button
                            type="button"
                            className={selectedFolderId === undefined ? 'is-active' : ''}
                            onClick={() => chooseFolder(undefined)}
                          >
                            Все диалоги
                          </button>
                          {folders.map((folder) => (
                            <button
                              key={folder.id}
                              type="button"
                              className={selectedFolderId === folder.id ? 'is-active' : ''}
                              onClick={() => chooseFolder(folder.id)}
                            >
                              {folder.title}
                            </button>
                          ))}
                        </div>

                        <label className="setup-source-search">
                          <span>Поиск по названию или username</span>
                          <input
                            type="search"
                            className="proj-field"
                            value={sourceSearch}
                            onChange={(e) => {
                              setSourceSearch(e.target.value)
                              setLastImportedCount(null)
                              setImportResult(null)
                            }}
                          />
                        </label>

                        <div className="setup-candidates-head">
                          <span>
                            Диалоги ({visibleCandidates.length}
                            {visibleCandidates.length !== candidates.length ? ` из ${candidates.length}` : ''})
                          </span>
                          {availableCandidates.length > 0 && (
                            <button type="button" onClick={toggleAllCandidates}>
                              {allSelected ? 'Снять всё' : 'Выбрать всё'}
                            </button>
                          )}
                        </div>

                        {candidatesQuery.isLoading && (
                          <div className="setup-loading">
                            <Spinner />
                            Загружаем диалоги...
                          </div>
                        )}

                        {candidatesQuery.isSuccess && candidates.length === 0 && (
                          <div className="setup-empty">В этой папке нет доступных диалогов.</div>
                        )}

                        {candidatesQuery.isSuccess && candidates.length > 0 && visibleCandidates.length === 0 && (
                          <div className="setup-empty">По запросу ничего не найдено.</div>
                        )}

                        {visibleCandidates.length > 0 && (
                          <div className="setup-candidates">
                            {visibleCandidates.map((candidate) => {
                              const disabled = candidate.already_added
                              const checked = selectedChatIds.has(candidate.chat_id)
                              return (
                                <label
                                  key={candidate.chat_id}
                                  className={`${disabled ? 'is-disabled' : ''}${checked ? ' is-checked' : ''}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    disabled={disabled}
                                    onChange={() => !disabled && toggleCandidate(candidate.chat_id)}
                                  />
                                  <span>
                                    <b>{candidate.title || `ID ${candidate.chat_id}`}</b>
                                    {candidate.username && <small>@{candidate.username}</small>}
                                  </span>
                                  {candidate.already_added && <i>уже добавлен</i>}
                                </label>
                              )
                            })}
                          </div>
                        )}

                        <div className="setup-import-footer">
                          {importResult && <span className="setup-ok">{importResult}</span>}
                          {applyImportMutation.isError && (
                            <span className="setup-error">
                              {apiErrorMessage(applyImportMutation.error, 'Ошибка импорта')}
                            </span>
                          )}
                          <button
                            type="button"
                            className="btn btn--primary btn--sm"
                            disabled={selectedImportIds.length === 0 || applyImportMutation.isPending}
                            onClick={() => selectedImportIds.length > 0 && applyImportMutation.mutate(selectedImportIds)}
                          >
                            {applyImportMutation.isPending
                              ? 'Импорт...'
                              : selectedImportIds.length > 0
                                ? `Импортировать выбранные (${selectedImportIds.length})`
                                : lastImportedCount
                                  ? `Импортировано: ${lastImportedCount}`
                                  : 'Импортировать выбранные'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="setup-current-sources">
                  <div>
                    <h3>Источники проекта</h3>
                    <span>{sourceCount} добавлено</span>
                  </div>
                  {sourcesQuery.isLoading && (
                    <div className="setup-loading">
                      <Spinner />
                      Загружаем источники...
                    </div>
                  )}
                  {!sourcesQuery.isLoading && activeSources.length === 0 && (
                    <div className="setup-empty">Пока нет источников. Добавьте вручную или импортируйте из Telegram.</div>
                  )}
                  {activeSources.length > 0 && (
                    <div className="setup-source-list">
                      {activeSources.slice(0, 12).map((source) => (
                        <span key={source.id} className="setup-source-pill">
                          <b>
                            {sourceTitle(source)}
                            {source.username ? <small>@{source.username}</small> : null}
                          </b>
                          <button
                            type="button"
                            aria-label={`Удалить источник ${sourceTitle(source)}`}
                            disabled={removeSourceMutation.isPending}
                            onClick={() => removeSourceMutation.mutate(source.id)}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      {activeSources.length > 12 && <span>+{activeSources.length - 12} ещё</span>}
                    </div>
                  )}
                  {removeSourceMutation.isError && (
                    <span className="setup-error">
                      {apiErrorMessage(removeSourceMutation.error, 'Не удалось удалить источник')}
                    </span>
                  )}
                </div>
              </section>
            )}

            {detail && step === 'launch' && (
              <section className="setup-panel">
                <div className="setup-panel__head">
                  <span>Шаг {stepIndex + 1} из {SETUP_STEPS.length}</span>
                  <h2>Все готово к запуску</h2>
                  <p>После запуска SENSOR начнет мониторить выбранные источники. Исторический скан можно включить отдельно после запуска.</p>
                </div>

                <div className="setup-checklist">
                  <div className="is-done"><b>AI-черновик</b><span>Критерии подтверждены</span></div>
                  <div className="is-done"><b>Telegram-доступ</b><span>{detail.userbot_display_name || detail.userbot_label || 'Выбран'}</span></div>
                  <div className="is-done"><b>Источники</b><span>{sourceCount} добавлено</span></div>
                </div>

                {lifecycleErr && <span className="setup-error">{lifecycleErr}</span>}
              </section>
            )}
          </main>
        </div>

        <footer className="setup-foot">
          <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>
            Закрыть
          </button>
          <div>
            {stepIndex > 0 && (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => setStep(SETUP_STEPS[Math.max(0, stepIndex - 1)].id)}
              >
                Назад
              </button>
            )}
            {step !== 'launch' ? (
              <button
                type="button"
                className="btn btn--primary btn--sm"
                disabled={
                  (step === 'access' && !hasUserbot) ||
                  (step === 'sources' && !hasSources) ||
                  aiEditing
                }
                onClick={goNext}
              >
                {step === 'review'
                  ? 'Все верно, продолжить'
                  : step === 'access'
                    ? 'Продолжить'
                    : 'Перейти к запуску'}
              </button>
            ) : (
              <button
                type="button"
                className="btn btn--primary btn--sm"
                disabled={activateMutation.isPending || !hasUserbot || !hasSources}
                onClick={() => {
                  setLifecycleErr(null)
                  activateMutation.mutate()
                }}
              >
                {activateMutation.isPending ? 'Запускаем...' : 'Запустить мониторинг'}
              </button>
            )}
          </div>
        </footer>
      </div>

      <ConnectUserbotModal
        isOpen={connectUserbotOpen}
        onClose={() => setConnectUserbotOpen(false)}
        onConnected={() => {
          void queryClient.invalidateQueries({ queryKey: ['me', 'userbots'] })
          setConnectUserbotOpen(false)
        }}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// HealthBar (preserved from original)
// ---------------------------------------------------------------------------

function HealthBar({ value }: { value: number }) {
  return (
    <div
      className="h-1.5 w-full rounded-full"
      style={{ background: 'rgba(11,15,20,0.06)' }}
    >
      <div
        className="h-full rounded-full"
        style={{
          width: `${Math.min(100, Math.round(value * 100))}%`,
          background: 'var(--teal)',
        }}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// ProjectCard
// ---------------------------------------------------------------------------

function ProjectCard({
  project,
  projectNumbers,
  summary,
  health,
  isSelected,
  onClick,
  onSetup,
}: {
  project: ProjectResponse
  projectNumbers: Map<number, number>
  summary: ProjectSummaryResponse | undefined
  health: ProjectHealthResponse | undefined
  isSelected: boolean
  onClick: () => void
  onSetup: () => void
}) {
  const isActive = project.status === 'active'
  const isDraft = project.status === 'draft'

  return (
    <div
      className="p-5"
      style={{
        borderRadius: 'var(--r-lg)',
        border: `1px solid ${isSelected ? 'var(--accent)' : 'var(--border, var(--line))'}`,
        background: 'var(--surface)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        cursor: 'pointer',
        transition: 'border-color .15s ease, box-shadow .15s ease',
        boxShadow: isSelected ? '0 0 0 3px var(--accent-soft)' : undefined,
      }}
      onClick={onClick}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h2 className="truncate font-semibold" style={{ color: 'var(--ink)' }}>
            {projectLabel(project, projectNumbers)}
          </h2>
          <p className="mt-0.5 text-xs" style={{ color: 'var(--muted)' }}>
            Обновлён:{' '}
            {new Date(project.updated_at).toLocaleDateString('ru-RU')}
          </p>
          {(project.userbot_display_name || project.userbot_label) && (
            <p className="mt-1 truncate text-xs" style={{ color: 'var(--ink-3)' }}>
              Телеграм доступ: {project.userbot_display_name || project.userbot_label}
            </p>
          )}
        </div>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${isActive ? 'bg-green-50 text-green-700' : ''}`}
          style={!isActive ? { background: 'rgba(11,15,20,0.04)', color: 'var(--ink-2)' } : undefined}
        >
          {project.status}
          {project.is_default && ' · по умолч.'}
        </span>
      </div>

      {/* Stats grid */}
      {summary && (
        <div className="mb-4 grid grid-cols-3 gap-3 text-center">
          <div>
            <p className="text-xl font-bold" style={{ color: 'var(--ink)' }}>{summary.total_leads}</p>
            <p className="text-xs" style={{ color: 'var(--muted)' }}>лидов</p>
          </div>
          <div>
            <p
              className="text-xl font-bold"
              style={{ color: summary.hot_leads > 0 ? '#D97706' : 'var(--ink)' }}
            >
              {summary.hot_leads}
            </p>
            <p className="text-xs" style={{ color: 'var(--muted)' }}>горячих</p>
          </div>
          <div>
            <p className="text-xl font-bold" style={{ color: 'var(--ink)' }}>{summary.sources_count}</p>
            <p className="text-xs" style={{ color: 'var(--muted)' }}>источников</p>
          </div>
        </div>
      )}

      {/* Health */}
      {health && (
        <div className="mb-4 space-y-1.5">
          <div className="flex justify-between text-xs" style={{ color: 'var(--muted)' }}>
            <span>Источники с лидами</span>
            <span>{health.sources_with_leads} / {health.sources_total}</span>
          </div>
          <HealthBar
            value={health.sources_total > 0 ? health.sources_with_leads / health.sources_total : 0}
          />
          {health.recommendation && (
            <p className="text-xs mt-2" style={{ color: '#D97706' }}>{health.recommendation}</p>
          )}
        </div>
      )}

      {/* Actions */}
      <div
        className="flex gap-2 pt-2 border-t mt-auto"
        style={{ borderColor: 'var(--border, var(--line))' }}
      >
        {isDraft ? (
          <button
            type="button"
            className="flex-1 rounded-lg px-3 py-1.5 text-center text-sm font-medium"
            style={{
              background: 'oklch(0.97 0.02 195)',
              color: 'var(--accent)',
              border: 'none',
              cursor: 'pointer',
            }}
            onClick={(e) => {
              e.stopPropagation()
              onSetup()
            }}
          >
            Продолжить настройку
          </button>
        ) : (
          <>
            <Link
              to={`/leads?project=${project.id}`}
              className="flex-1 rounded-lg px-3 py-1.5 text-center text-sm font-medium"
              style={{
                background: 'oklch(0.97 0.02 195)',
                color: 'var(--accent)',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.background = 'oklch(0.94 0.04 195)'
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.background = 'oklch(0.97 0.02 195)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              Лиды
            </Link>
            <Link
              to={`/sources?project=${project.id}`}
              className="flex-1 rounded-lg border px-3 py-1.5 text-center text-sm"
              style={{
                borderColor: 'var(--border, var(--line))',
                color: 'var(--ink-2)',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(11,15,20,0.03)'
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.background = ''
              }}
              onClick={(e) => e.stopPropagation()}
            >
              Источники
            </Link>
          </>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// ProjectsPage
// ---------------------------------------------------------------------------

export function ProjectsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [setupProjectId, setSetupProjectId] = useState<number | null>(null)
  const queryProjectId = Number(searchParams.get('project')) || null
  const { layoutRef, drawerWidth, splitStyle, startResize } = useProjectSplitResize()

  useEffect(() => {
    if (queryProjectId) {
      setShowCreate(false)
      setSelectedId(queryProjectId)
    }
  }, [queryProjectId])

  function selectProject(id: number | null) {
    setSelectedId(id)
    const next = new URLSearchParams(searchParams)
    if (id == null) next.delete('project')
    else next.set('project', String(id))
    setSearchParams(next, { replace: true })
  }

  function openSetup(id: number) {
    setShowCreate(false)
    selectProject(id)
    setSetupProjectId(id)
  }

  const { data: projects, isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: api.getProjects,
  })
  const projectNumbers = buildProjectNumberMap(projects)

  const { data: summaries } = useQuery({
    queryKey: ['project-summaries', projects?.map((p) => p.id)],
    queryFn: () =>
      Promise.all((projects ?? []).map((p) => api.getProjectSummary(p.id))),
    enabled: !!projects?.length,
  })

  const { data: healths } = useQuery({
    queryKey: ['project-healths', projects?.map((p) => p.id)],
    queryFn: () =>
      Promise.all((projects ?? []).map((p) => api.getProjectHealth(p.id))),
    enabled: !!projects?.length,
  })

  const hasPanel = selectedId !== null

  return (
    <div className="p-8">
      {/* Page head */}
      <div className="ds-pagehead mb-6">
        <h1>Проекты</h1>
        <div className="ds-pagehead__actions">
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => {
              setShowCreate(true)
              selectProject(null)
            }}
          >
            + Новый проект
          </button>
        </div>
      </div>

      {/* Create form */}
      {showCreate && (
        <CreateProjectForm
          onCancel={() => setShowCreate(false)}
          onCreated={(project) => {
            setShowCreate(false)
            selectProject(project.id)
            setSetupProjectId(project.id)
          }}
        />
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--muted)' }}>
          <Spinner />
          Загрузка…
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !projects?.length && !showCreate && (
        <div
          className="py-16 text-center"
          style={{
            borderRadius: 'var(--r-lg)',
            border: '1px dashed var(--line-2)',
            background: 'var(--surface)',
          }}
        >
          <p className="text-sm" style={{ color: 'var(--ink-2)' }}>
            Проектов пока нет. Нажмите{' '}
            <button
              type="button"
              onClick={() => {
                setShowCreate(true)
                selectProject(null)
              }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--accent)',
                fontSize: 'inherit',
                fontFamily: 'inherit',
                padding: 0,
                textDecoration: 'underline',
                textUnderlineOffset: 3,
              }}
            >
              + Новый проект
            </button>{' '}
            чтобы создать первый.
          </p>
        </div>
      )}

      {/* Main layout: grid + optional drawer */}
      <div
        ref={layoutRef}
        className={`proj-page-layout${hasPanel ? ' has-panel' : ''}`}
        style={hasPanel ? splitStyle : undefined}
      >
        <div className="grid gap-5 proj-page-grid">
          {projects?.map((p, i) => (
            <ProjectCard
              key={p.id}
              project={p}
              projectNumbers={projectNumbers}
              summary={summaries?.[i]}
              health={healths?.[i]}
              isSelected={p.id === selectedId}
              onClick={() => {
                setShowCreate(false)
                selectProject(p.id === selectedId ? null : p.id)
              }}
              onSetup={() => openSetup(p.id)}
            />
          ))}
        </div>

        {hasPanel && (
          <div
            className="proj-split-resizer"
            role="separator"
            aria-orientation="vertical"
            aria-valuemin={PROJECT_DRAWER_WIDTH_MIN}
            aria-valuemax={PROJECT_DRAWER_WIDTH_MAX}
            aria-valuenow={drawerWidth}
            title="Потяните, чтобы изменить ширину панели проекта"
            onPointerDown={startResize}
          />
        )}

        {hasPanel && selectedId !== null && (
          <ProjectDrawer
            key={selectedId}
            projectId={selectedId}
            projectNumbers={projectNumbers}
            onClose={() => selectProject(null)}
            onOpenSetup={() => openSetup(selectedId)}
            onDuplicated={(newProjectId) => selectProject(newProjectId)}
          />
        )}
      </div>

      {setupProjectId !== null && (
        <ProjectSetupWizard
          projectId={setupProjectId}
          projectNumbers={projectNumbers}
          onClose={() => setSetupProjectId(null)}
          onActivated={() => {
            setSetupProjectId(null)
            selectProject(setupProjectId)
          }}
        />
      )}
    </div>
  )
}
